import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useCallback, useEffect, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { IconArrowRightUpCircle } from '~/ui/core/icons';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { useTileNavigation } from '../../url-tiles';
import { useArchiveJobLevels } from '../api/use-archive-job-levels';
import { useArchiveJobRoles } from '../api/use-archive-job-roles';
import { useJobArchitectureItemPath } from '../api/use-job-architecture-item-path';
import type { JobItemDetails } from '../api/use-job-item-details';
import { LanguageWithIndicator } from '../components/LanguageWithIndicator';
import { SkeletonBreadcrumbs } from '../components/SkeletonLoaders';
import { useIsGenerating, useIsGeneratingSkills } from '../hooks/use-is-generating';
import { useNavigateToStandaloneProfile } from '../job-profile-standalone/hooks/use-navigate-to-standalone-profile';
import { ActiveLanguageProvider, useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { JSA_PRIMARY_DETAIL_TILE_SLOT_KEY } from '../url-tile-payloads';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { JobProfileContent } from './JobProfileContent';
import { ManualEditStoreProvider, useManualEditStore } from './ManualEditStoreProvider';
import { ProfileBreadcrumbs } from './ProfileBreadcrumbs';
import { SkillActionSwitcherProvider } from './SkillActionSwitcherProvider';
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard';

type DetailsContentProps = {
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly jobFamilyId?: string;
    readonly onActiveLanguageChange?: (language: LanguageCodeForJSA) => void;
    readonly onClose: () => void;
    readonly onToggleComments?: () => void;
    readonly parentRoleId?: string;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profile: JobProfileComponent;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

const Content: FunctionComponent<DetailsContentProps> = ({
    architectureId,
    isArchived,
    isCommentsOpen,
    isEditing,
    item,
    itemId,
    itemType,
    jobFamilyId,
    onActiveLanguageChange,
    onClose,
    onToggleComments,
    parentRoleId,
    permissions,
    profile,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}) => {
    const translate = useTranslate();
    const hasManualEdits = useManualEditStore((state) => state.hasManualEdits);
    const { navigate: navigateToStandalone } = useNavigateToStandaloneProfile();
    const { confirm } = useConfirm();
    const { mutateAsync: archiveJobRoles } = useArchiveJobRoles();
    const { mutateAsync: archiveJobLevels } = useArchiveJobLevels();
    const { closePrimaryDetailTile, hasCompareTile, openCompareTile, openSkillTile, updateJobProfileTileLanguage } =
        useJobArchitectureUrlTileActions();

    const { activeLanguage, availableLanguages, setActiveLanguage } = useActiveLanguage();

    useEffect(() => {
        onActiveLanguageChange?.(activeLanguage);
    }, [activeLanguage, onActiveLanguageChange]);

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA) => {
            setActiveLanguage(language);
            updateJobProfileTileLanguage(language);
        },
        [setActiveLanguage, updateJobProfileTileLanguage],
    );

    const isGeneratingTranslations = useIsGenerating(item?.profileGenerationInfo);
    const isGeneratingSkills = useIsGeneratingSkills(item?.profileGenerationInfo);
    const { data: itemPath, isLoading: isItemPathLoading } = useJobArchitectureItemPath(architectureId, itemId);

    const { cancelDiscard, confirmDiscard, isDiscardDialogOpen, requestDiscard } = useUnsavedChangesGuard({
        hasUnsavedChanges: isEditing && hasManualEdits,
    });

    const handleClose = useCallback(() => {
        requestDiscard(() => {
            setIsEditing(false);
            onClose();
        });
    }, [requestDiscard, setIsEditing, onClose]);

    const handleCompareVersions = useCallback(
        (versionA: string, versionB: string, preferredLanguage = activeLanguage): void => {
            openCompareTile({
                profileId: itemId,
                preferredLanguage,
                type: itemType,
                versionA,
                versionB,
            });
        },
        [activeLanguage, itemId, itemType, openCompareTile],
    );

    const handleOpenSkillDetails = useCallback(
        (
            skillId: string,
            proficiency: SkillProficiencyLevel | null | undefined,
            _availableItems: readonly { readonly id: string }[],
        ) => {
            openSkillTile(skillId, activeLanguage, proficiency);
        },
        [activeLanguage, openSkillTile],
    );

    const handleOpenSeparatePage = useCallback(() => {
        requestDiscard(() => {
            setIsEditing(false);
            navigateToStandalone({ architectureId, itemId, itemType, preferredLanguage: activeLanguage });
        });
    }, [requestDiscard, setIsEditing, navigateToStandalone, architectureId, itemId, itemType, activeLanguage]);

    const breadcrumbs = useMemo(() => {
        if (isArchived === true) {
            return undefined;
        }

        const clusterId = itemPath?.cluster;
        const familyId = itemPath?.family;

        if (isItemPathLoading || !is.nonEmptyString(clusterId) || !is.nonEmptyString(familyId)) {
            return <SkeletonBreadcrumbs />;
        }

        return (
            <ProfileBreadcrumbs
                architectureId={architectureId}
                clusterId={clusterId}
                currentItemTitle={
                    itemType === 'job-level'
                        ? translate('pages.job-architecture.job-level')
                        : translate('pages.job-architecture.job-role')
                }
                familyId={familyId}
                parentRoleId={itemType === 'job-level' ? (itemPath?.role ?? parentRoleId) : undefined}
            />
        );
    }, [architectureId, isArchived, isItemPathLoading, itemPath, itemType, parentRoleId, translate]);

    const originLanguageId = item.versionInfo?.translationOriginLanguage;

    const primaryDetailTileKey = `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${architectureId}.${itemId}`;
    const tileNavigation = useTileNavigation(primaryDetailTileKey);

    const header = useMemo(
        () => (
            <SidepanelHeader
                actions={
                    <Fragment>
                        <Button
                            aria-label={translate('pages.job-architecture-v2.open-separate-page')}
                            iconOnly={true}
                            size="sm"
                            variant="secondary"
                            onClick={handleOpenSeparatePage}
                        >
                            <IconArrowRightUpCircle />
                        </Button>
                        <div id="header-options-slot" />
                    </Fragment>
                }
                activeLanguage={
                    activeLanguage === undefined
                        ? undefined
                        : translate(`common.languages.${activeLanguage}` as TranslationKey)
                }
                disableArrows={is.undefined(tileNavigation)}
                isGenerating={isGeneratingTranslations || isGeneratingSkills}
                isLanguagePickerDisabled={isEditing}
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
                languages={availableLanguages.map((language) => ({
                    ...language,
                    name: (
                        <LanguageWithIndicator
                            language={language}
                            originLanguageId={originLanguageId}
                            originLanguageLabel={translate('pages.job-architecture-v2.origin-language-label')}
                            originLanguageTooltip={translate('pages.job-architecture-v2.origin-language-tooltip')}
                            translatedLanguageTooltip={translate(
                                'pages.job-architecture-v2.translated-with-ai-tooltip',
                            )}
                        />
                    ),
                }))}
                setActiveLanguage={handleLanguageChange}
                onClose={handleClose}
            />
        ),
        [
            handleOpenSeparatePage,
            translate,
            activeLanguage,
            isGeneratingTranslations,
            isGeneratingSkills,
            isEditing,
            availableLanguages,
            originLanguageId,
            handleLanguageChange,
            handleClose,
            tileNavigation,
        ],
    );

    const handleArchive = useCallback(async (): Promise<void> => {
        await confirm({
            description: (
                <Fragment>
                    {item.type === 'job-role'
                        ? translate('pages.job-architecture-v2.archive-job-roles-description')
                        : translate('pages.job-architecture-v2.archive-job-levels-description')}
                    <ul>
                        <li key={item.id}>{item.title}</li>
                    </ul>
                </Fragment>
            ),
            title: translate('pages.job-architecture-v2.archive-title'),
            type: 'default',
        });

        await (item.type === 'job-role'
            ? archiveJobRoles({ jobRoles: [item] })
            : archiveJobLevels({
                  jobLevels: [item],
              }));
        closePrimaryDetailTile();
    }, [confirm, translate, item, archiveJobRoles, archiveJobLevels, closePrimaryDetailTile]);

    const commentable = useMemo(
        () =>
            is.undefined(onToggleComments)
                ? undefined
                : { isOpen: isCommentsOpen ?? false, onToggle: onToggleComments },
        [isCommentsOpen, onToggleComments],
    );

    return (
        <Fragment>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.confirm')}
                confirmButtonVariant="default"
                description={translate('dialogs.discard.content')}
                open={isDiscardDialogOpen}
                title={translate('dialogs.discard.title')}
                onCancel={cancelDiscard}
                onConfirm={confirmDiscard}
            />
            <JobProfileContent
                architectureId={architectureId}
                breadcrumbs={breadcrumbs}
                commentable={commentable}
                header={header}
                isArchived={isArchived}
                isComparingOpen={hasCompareTile()}
                isEditing={isEditing}
                item={item}
                itemId={itemId}
                itemType={itemType}
                jobFamilyId={jobFamilyId}
                parentRoleId={parentRoleId}
                permissions={permissions}
                profile={profile}
                selectedVersion={selectedVersion}
                setActiveVersion={setActiveVersion}
                setIsEditing={setIsEditing}
                onArchive={handleArchive}
                onCompareVersions={handleCompareVersions}
                onOpenSeparatePage={handleOpenSeparatePage}
                onOpenSkillDetails={handleOpenSkillDetails}
            />
        </Fragment>
    );
};

export const DetailsContent: FunctionComponent<DetailsContentProps> = (props) => {
    return (
        <ActiveLanguageProvider preferredLanguage={props.preferredLanguage} profiles={[props.profile]}>
            <SkillActionSwitcherProvider>
                <ManualEditStoreProvider>
                    <Content {...props} />
                </ManualEditStoreProvider>
            </SkillActionSwitcherProvider>
        </ActiveLanguageProvider>
    );
};
